import React, { useState, useEffect, useRef } from "react"
import HelpCenterBrowser from "./HelpCenterBrowser"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { ChevronDownIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"

/**
 * Sidebar Component with final fix for jumping issue
 * Uses fixed position content container to eliminate jumping
 * Added Cancel button at bottom of mobile menu
 * Fixed mobile article selection to include full path like desktop version
 * Added improved slide-down-to-close functionality
 */
const Sidebar = ({
  rootCategoriesData,
  onArticleSelect,
  onCategorySelect,
  expandedCategories,
  currentPath,
}) => {
  const texts = TextBundle({ id: "help-center" })
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const dropdownRef = useRef(null)
  const panelRef = useRef(null)
  
  // Toggle mobile dropdown
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  // Pass-through handlers to ensure consistent behavior
  const handleCategorySelect = (categorySlug, parentPath) => {
    onCategorySelect(categorySlug, parentPath)
  }

  // Modified to maintain the same behavior as desktop version
  // Updated to pass both article and parentPath to onArticleSelect
  const handleArticleSelect = (article, parentPath) => {
    // Direct pass-through with both arguments to ensure consistent behavior
    onArticleSelect(article, parentPath)
    // Close the mobile menu after selection
    setIsMobileMenuOpen(false)
  }

  // Handle body scroll lock
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [isMobileMenuOpen])

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMobileMenuOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest(".mobile-dropdown-toggle")
      ) {
        setIsMobileMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isMobileMenuOpen])

  // Setup swipe detection using Vanilla JS for better performance and reliability
  useEffect(() => {
    if (!isMobileMenuOpen || !dropdownRef.current) return
    
    let startY = 0
    let startTime = 0
    let moving = false
    let didSwipeDown = false
    
    // Get the main container element
    const panel = dropdownRef.current
    if (!panel) return
    
    // Main swipe detection logic
    const detectSwipe = () => {
      // Simple swipe detector
      const handleTouchStart = (e) => {
        startY = e.touches[0].clientY
        startTime = new Date().getTime()
        moving = true
        didSwipeDown = false
        
        // Immediately set transition to none for instant response
        panel.style.transition = 'none'
      }
      
      const handleTouchMove = (e) => {
        if (!moving) return
        
        const currentY = e.touches[0].clientY
        const deltaY = currentY - startY
        
        // Only handle downward swipes
        if (deltaY > 0) {
          e.preventDefault()
          
          // Apply transform directly to the main panel container
          panel.style.transform = `translateY(${deltaY}px)`
          
          // If dragged down more than 80px or quick flick, consider it a swipe
          const timeDiff = new Date().getTime() - startTime
          const isQuickFlick = timeDiff < 300 && deltaY > 40
          
          if (deltaY > 80 || isQuickFlick) {
            didSwipeDown = true
          }
        }
      }
      
      const handleTouchEnd = () => {
        if (!moving) return
        moving = false
        
        if (didSwipeDown) {
          // If swiped down, close with animation
          panel.style.transition = 'transform 300ms ease-out'
          panel.style.transform = 'translateY(100%)'
          
          // Wait for animation to complete before changing state
          setTimeout(() => {
            setIsMobileMenuOpen(false)
          }, 300)
        } else {
          // Reset position with animation
          panel.style.transition = 'transform 200ms ease-out'
          panel.style.transform = 'translateY(0)'
        }
      }
      
      // Add event listeners
      panel.addEventListener('touchstart', handleTouchStart, { passive: false })
      panel.addEventListener('touchmove', handleTouchMove, { passive: false })
      panel.addEventListener('touchend', handleTouchEnd)
      
      // Return cleanup function
      return () => {
        panel.removeEventListener('touchstart', handleTouchStart)
        panel.removeEventListener('touchmove', handleTouchMove)
        panel.removeEventListener('touchend', handleTouchEnd)
      }
    }
    
    // Start detection and get cleanup function
    const cleanupSwipeDetection = detectSwipe()
    
    // Clean up on dismount or when menu closes
    return () => {
      if (cleanupSwipeDetection) {
        cleanupSwipeDetection()
      }
    }
  }, [isMobileMenuOpen])

  // Desktop version
  const DesktopSidebar = () => (
    <div className="hidden lg:block w-1/4 lg:w-[346px] xl:w-[360px] border-r border-bordered flex-shrink-0 overflow-y-auto pr-10">
      {/* "Articles" heading */}
      <div className="sticky top-0 pt-2 pb-4">
        <Typography variant="h3" className="text-text text-left">
          {texts?.sidebar_heading || "Articles"}
        </Typography>
      </div>

      {rootCategoriesData && (
        <HelpCenterBrowser
          rootCategoriesData={rootCategoriesData}
          onArticleSelect={onArticleSelect}
          onCategorySelect={onCategorySelect}
          expandedCategories={expandedCategories || {}}
          currentPath={currentPath}
          isMobile={false}
        />
      )}
    </div>
  )

  // Mobile version with fixed position approach to eliminate jumping
  const MobileSidebar = () => {
    // Mobile menu styles with simple animations
    const mobileStyles = `
      .mobile-overlay {
        transition: opacity 250ms ease-out;
        opacity: 0;
      }

      .mobile-overlay.visible {
        opacity: 0.15;
      }

      .mobile-panel {
        transition: transform 250ms cubic-bezier(0.16, 1, 0.3, 1);
        transform: translateY(100%);
      }

      .mobile-panel.visible {
        transform: translateY(0);
      }
      
      .drag-handle {
        width: 48px;
        height: 5px;
        background-color: #D1D5DB;
        border-radius: 9999px;
        margin: 0 auto 16px;
      }
    `

    return (
      <div className="block lg:hidden w-full mb-6 md:mb-8">
        {/* Animation styles */}
        <style dangerouslySetInnerHTML={{ __html: mobileStyles }} />

        {/* Toggle button */}
        <div
          onClick={toggleMobileMenu}
          className="max-w-fit h-12 flex items-center justify-start gap-2 bg-paper rounded-full border border-secondary cursor-pointer px-4 mobile-dropdown-toggle"
        >
          <Typography variant="h3" className="text-text">
            {texts?.sidebar_heading || "Articles"}
          </Typography>

          <ChevronDownIcon
            className={`w-5 h-5 text-gray_700 transition-transform ${
              isMobileMenuOpen ? "rotate-180" : ""
            }`}
          />
        </div>

        {/* Always render the mobile menu container but toggle visibility with CSS */}
        <div
          className={`fixed inset-0 bg-black z-40 mobile-overlay ${
            isMobileMenuOpen ? "visible" : ""
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
          style={{ display: isMobileMenuOpen ? "block" : "none" }}
        />

        <div
          ref={dropdownRef}
          className={`fixed bottom-0 left-0 right-0 bg-paper rounded-t-[24px] z-50 mobile-panel ${
            isMobileMenuOpen ? "visible" : ""
          }`}
          style={{
            height: "70vh",
            display: isMobileMenuOpen ? "block" : "none",
          }}
        >
          <div className="h-full">
            {/* Fixed header with drag handle for visual indication */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-paper px-5 pt-5 pb-2 rounded-t-[24px]">
              <div className="drag-handle" />
              <Typography variant="h3" className="text-text mb-2">
                {texts?.sidebar_heading || "Articles"}
              </Typography>
            </div>

            {/* Fixed dimensions scrollable content that never jumps */}
            <div
              className="absolute left-0 right-0 px-5 overflow-y-auto"
              style={{
                top: "80px", // Match header height
                bottom: "64px", // Adjust for Cancel button height
                overscrollBehavior: "contain",
              }}
            >
              {rootCategoriesData && (
                <HelpCenterBrowser
                  rootCategoriesData={rootCategoriesData}
                  onArticleSelect={handleArticleSelect} // Use handleArticleSelect instead of onArticleSelect
                  onCategorySelect={handleCategorySelect}
                  expandedCategories={expandedCategories || {}}
                  currentPath={currentPath}
                  isMobile={true}
                />
              )}
              {/* Add some bottom padding to ensure scrollability */}
              <div className="h-8"></div>
            </div>

            {/* Cancel button at bottom */}
            <div className="absolute bottom-0 left-0 right-0 w-full bg-paper px-4 py-6">
              <Button
                onClick={() => setIsMobileMenuOpen(false)}
                // variant="text_btn_primary_m"
                variant="secondary_l"
                className="w-full max-w-[360px] mx-auto"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <MobileSidebar />
      <DesktopSidebar />
    </>
  )
}

export default Sidebar